import { useEffect, useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import type { ColumnSelectionOptions } from "app/pages/sales-intelligence/common-components/column-picker";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import useGroupedTechnologiesCategoryMapQuery from "app/pages/sales-intelligence/modules/technology/queries/useGroupedTechnologiesCategoryMapQuery";
import type ColumnSuggestion from "app/pages/sales-intelligence/sub-modules/column-suggestions/models/ColumnSuggestion";
import columnByFilterSuggestionsService from "app/pages/sales-intelligence/sub-modules/common/services/company-search-column-suggestions/columnByFilterSuggestionsService";

type SuggestionsFunction = (
    filters: Partial<LeadsSearchAllFiltersTypes>,
    selectedColumnNames: readonly string[],
) => readonly ColumnSuggestion[];

/**
 * Hook that manages the initialization of the column suggestions service.
 * Handles the async initialization with idleReduce for performance with large datasets.
 */
export const useCreateColumnSuggestions = (availableColumns: ColumnSelectionOptions): SuggestionsFunction | null => {
    const [suggestionsFunction, setSuggestionsFunction] = useState<SuggestionsFunction | null>(null);

    const { data: groupedTechnologiesCategoryMap } = useGroupedTechnologiesCategoryMapQuery();

    const canInitialize = !!(availableColumns?.length && groupedTechnologiesCategoryMap);

    useEffect(() => {
        if (!canInitialize) {
            setSuggestionsFunction(null);

            return;
        }

        let cancelled = false;

        const initializeService = async () => {
            try {
                const initializedService = await columnByFilterSuggestionsService.initialize(
                    availableColumns,
                    groupedTechnologiesCategoryMap,
                );

                if (!cancelled) {
                    const createSuggestions =
                        columnByFilterSuggestionsService.createColumnSuggestionsFunction(initializedService);

                    setSuggestionsFunction(() => createSuggestions);
                }
            } catch (error) {
                if (!cancelled) {
                    setSuggestionsFunction(null);
                }

                Sentry.errorTracker({
                    message: "Error initializing column suggestions service",
                    e: error,
                    level: "error",
                });
            }
        };

        void initializeService();

        return () => {
            cancelled = true;
        };
    }, [canInitialize, availableColumns, groupedTechnologiesCategoryMap]);

    return suggestionsFunction;
};
